import { Location } from "@angular/common";
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  NgModel,
  Validators,
  FormArray,
} from "@angular/forms";
import { NavigationExtras, Router } from "@angular/router";
import { User } from "@core/models";
import { CompanyService, NgxToastrService, UserService, UtilsService } from "@core/services";
import {
  NgbDate,
  NgbDateParserFormatter,
  NgbDateStruct,
  NgbInputDatepicker,
  NgbModal,
} from "@ng-bootstrap/ng-bootstrap";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { AppState } from "app/app.reducer";
import cities from "assets/utils/cities.json";
import hotels from "assets/utils/hotels.json";
import * as moment from "moment";
import { Subscription } from "rxjs";
import { take } from "rxjs/operators";
import { BookedService } from "../../../../core/services/booked.service";
import { FormControl } from '@angular/forms';

interface RequestHotelTraveler {
  type?: 'internal' | 'external';
  uuid?: string;
  name: string;
  lastname: string;
}

@Component({
  selector: "app-request-custom-hotel",
  templateUrl: "./request-custom-hotel.component.html",
  styleUrls: ["./request-custom-hotel.component.scss"],
})
export class RequestCustomHotelComponent implements OnInit, OnDestroy {
  public get ngbDateParser(): NgbDateParserFormatter {
    return this._ngbDateParser;
  }
  public set ngbDateParser(value: NgbDateParserFormatter) {
    this._ngbDateParser = value;
  }
  @Output() fromDateEmit = new EventEmitter();
  @Output() toDateEmit = new EventEmitter();
  hoveredDate: NgbDateStruct;
  @ViewChild("d1") bookedInput: NgbInputDatepicker;
  @ViewChild("bookedRange") bookedRange: ElementRef;
  @ViewChild(NgModel) datePick: NgModel;
  @Input() isModal?;
  @Input() userUuid?;
  @Input() companyUuid?;
  @Output() closeEvent? = new EventEmitter();
  subscriptions: Subscription[] = [];
  canRequestCustomBooking: boolean;
  canRequestCustomFlight: boolean;
  canRequestCustomTrain: boolean;
  canRequestCustomCar: boolean;
  canRequestCustomAirbnb: boolean;
  authUserUuid = "";
  userImageBase64: string;
  loading = false;
  form: UntypedFormGroup;
  date = new Date();
  bookedUserAccessType: string;
  minDateNgStruct = {
    year: this.date.getFullYear(),
    month: this.date.getMonth() + 1,
    day: this.date.getDate(),
  };
  maxDateNgStruct = {
    year: this.date.getFullYear() + 20,
    month: this.date.getMonth() + 1,
    day: this.date.getDate(),
  };
  hotelsList = [];
  citiesList = [];
  city = "";
  user: User;
  userAdmin: any;
  userType: string;
  userToBook: string;
  isAdmin: boolean = false;
  isInvalid: boolean = false;
  companyUsers: { name: string; lastname: string; uuid: string }[] = [];
  defaultUser: any;
  filteredUsers: { name: string; lastname: string; uuid: string }[] = [];
  filteredUsersWithoutDefault: { name: string; lastname: string; uuid: string }[] = [];
  canUseTRM: boolean;
  @ViewChildren('input') searchInputs!: QueryList<ElementRef<HTMLInputElement>>;
  backendValidationErrors: Record<string, string> = {};
  externalGuestModeByRoom: { [roomIndex: number]: boolean } = {};
  externalGuestNameByRoom: { [roomIndex: number]: string } = {};
  externalGuestLastnameByRoom: { [roomIndex: number]: string } = {};

  constructor(
    private bookedService: BookedService,
    private formBuilder: UntypedFormBuilder,
    public translate: TranslateService,
    private ngxToastrService: NgxToastrService,
    private _ngbDateParser: NgbDateParserFormatter,
    private utilsService: UtilsService,
    private userService: UserService,
    private router: Router,
    private modalService: NgbModal,
    private store: Store<AppState>,
    private location: Location,
    private companyService: CompanyService,
  ) {
    this.subscriptions.push(
      this.store.select("auth").subscribe((s: any) => {
        if (s.user.type?.toUpperCase() === "USER") {
          this.userUuid = s.user.uuid;
          this.authUserUuid = s.user.uuid;
        }
        if (s.user && s.user.type?.toUpperCase() !== "USER") {
          this.isAdmin = true;
          this.userAdmin = s.user;
          this.canUseTRM = s.user.type
            ? s.user.plan && s.user.plan.permissions.canUseTRM
            : s.user.user.plan && s.user.user.plan.permissions.canUseTRM;
        }
        this.userToBook = localStorage.getItem("userToBook")
          ? this.utilsService.decrypt(localStorage.getItem("userToBook"))
          : null;
        this.userType = s.user.type;
      })
    );
  }

  ngOnInit() {
    if (
      window.history.state.canRequestCustomBooking !== null &&
      window.history.state.canRequestCustomCar !== null &&
      window.history.state.canRequestCustomFlight !== null &&
      window.history.state.canRequestCustomTrain !== null &&
      window.history.state.canRequestCustomAirbnb !== null
    ) {
      this.canRequestCustomBooking = window.history.state.canRequestCustomBooking;
      this.canRequestCustomCar = window.history.state.canRequestCustomCar;
      this.canRequestCustomFlight = window.history.state.canRequestCustomFlight;
      this.canRequestCustomTrain = window.history.state.canRequestCustomTrain;
      this.canRequestCustomAirbnb = window.history.state.canRequestCustomAirbnb;
      this.userUuid = window.history.state.userUuid;
      this.companyUuid = window.history.state.companyUuid;
      this.userImageBase64 = window.history.state.userImageBase64;
    } else {
      this.router.navigate(["/custom-services"]);
    }
    this.getCompanyUsers();
    if (
      this.userUuid &&
      (this.userUuid !== null || this.userUuid !== undefined)
    ) {
      this.userService
        .getUser(this.userUuid)
        .pipe(take(1))
        .subscribe((user: User) => {
          this.bookedUserAccessType = user.type;
          this.user = user;
          this.defaultUser = {
            name: this.user.name,
            lastname: this.user.lastname,
            uuid: this.user.uuid
          };
          this.form.get("defaultTraveler").setValue([{ 
            type: 'internal',
            uuid: this.defaultUser.uuid, 
            name: this.defaultUser.name, 
            lastname: this.defaultUser.lastname 
          }]);
        });
    }

    this.form = this.formBuilder.group({
      hotel: [null, Validators.required],
      city: [null, [Validators.required]],
      checkIn: [null, [Validators.required]],
      checkOut: [null, [Validators.required]],
      comments: [""],
      rooms: [1, Validators.min(1)],
      userUuid: this.userUuid,
      companyUuid: this.companyUuid,
      defaultTraveler: this.formBuilder.control([], Validators.required),
      travelersPerRoom: this.formBuilder.array([]),
    });

    this.form.get("rooms").valueChanges.subscribe(value => {
      this.updateTravelersPerRoom(value);
    });
  }

  updateTravelersPerRoom(roomCount: number) {
    const travelersArray = this.getTravelersPerRoom;
    const currentValues = travelersArray.value;
    while (travelersArray.length < roomCount - 1) {
      travelersArray.push(this.formBuilder.control([], Validators.required));
    }
    while (travelersArray.length > roomCount - 1) {
      travelersArray.removeAt(travelersArray.length - 1);
    }
    travelersArray.controls.forEach((control, index) => {
      if (currentValues[index]) {
        control.setValue(currentValues[index]);
      }
    });
  }
  
  get getTravelersPerRoom(): FormArray {
    return this.form.get("travelersPerRoom") as FormArray;
  }

  compareUsers(user1: any, user2: any): boolean {
    if (!user1 || !user2) {
      return user1 === user2;
    }

    return user1.uuid === user2.uuid;
  }

  onSubmit() {
    this.isInvalid = false;
    this.loading = true;
    const body = this.form.getRawValue();
    const allTravelersPerRoom = [
      this.form.get("defaultTraveler")?.value,
      ...this.getTravelersPerRoom?.value
    ];
    const formattedTravelersPerRoom = allTravelersPerRoom.map((room, index) => ({
      roomNumber: index + 1,
      travelers: (room || []).map((traveler) => {
        if (traveler?.type === 'external') {
          return {
            type: 'external',
            uuid: traveler.uuid,
            name: traveler.name || '',
            lastname: traveler.lastname || '',
          };
        }

        return {
          type: 'internal',
          uuid: traveler.uuid,
          name: traveler.name || '',
          lastname: traveler.lastname || '',
        };
      })
    }));
    body["travelersPerRoom"] = formattedTravelersPerRoom;
    const totalTravelers = body["travelersPerRoom"]
      ? body["travelersPerRoom"].reduce((acc, room) => acc + (room.travelers ? room.travelers.length : 0), 0)
      : 0;

    const formatDate = (ngbDate: NgbDate): string =>
      `${ngbDate.year}-${String(ngbDate.month).padStart(2, '0')}-${String(ngbDate.day).padStart(2, '0')}`;
  
      body["checkIn"] = moment(formatDate(body["checkIn"]))
        .format("DD-MM-YYYY");
  
      body["checkOut"] = moment(formatDate(body["checkOut"]))
        .format("DD-MM-YYYY");
    body[
      "comments"
    ] = `(comments): ${body["comments"]} + (rooms): ${body["rooms"]} + (people): ${totalTravelers}`;

    if (this.user) {
      body["holder"] = { name: this.user.name, surname: this.user.lastname };
    }

    delete body.defaultTraveler
    // delete body.rooms;

    this.subscriptions.push(
      this.bookedService.sendCustomHotelRequest(body).subscribe(
        (s) => {
          this.subscriptions.push(
            this.translate
              .get(["common.congrat", "hotel.custom.request.success"])
              .subscribe((result) => {
                this.loading = false;
                this.ngxToastrService.typeSuccess(
                  result["common.congrat"],
                  result["hotel.custom.request.success"]
                );
                if (this.isModal) {
                  this.closeEvent.emit(s);
                } else {
                  const route = this.utilsService.getNavigationRoute(this.userAdmin || this.user, null, this.canUseTRM);
                  if (route) {
                    this.router.navigate([route]);
                  }
                }
              })
          );
        },
        (err) => {
          this.loading = false;

          const errorBody = err?.error || {};
          const validationErrors = Array.isArray(errorBody.validationErrors)
            ? errorBody.validationErrors
            : [];

          if (err.status === 422 && validationErrors.length > 0) {
            const validationMessage = this.utilsService.getValidationErrorsMessage(
              validationErrors,
              'hotel'
            );

            this.applyBackendValidationErrors(validationErrors);

            this.ngxToastrService.typeInfo(
              'ERROR',
              validationMessage || errorBody.message || this.translate.instant('common.validation.form-invalid'),
              10000,
              { enableHtml: true }
            );

            return;
          }

          this.translate.get("hotel.custom.request.error").subscribe((result) => {
            this.ngxToastrService.typeInfo(
              result,
              errorBody.message || errorBody.text || this.translate.instant('common.some-error')
            );
          });
        }
      )
    );
  }

  get f() {
    return this.form.controls;
  }

  goBack() {
    this.location.back();
  }

  emitDates() {
    this.fromDateEmit.emit(this.form.controls["checkIn"].value);
    this.toDateEmit.emit(this.form.controls["checkOut"].value);
  }

  onDateSelection(date: NgbDate) {
    const checkIn = this.form.controls["checkIn"].value;
    const checkOut = this.form.controls["checkOut"].value;
  
    if (!checkIn) {
      this.form.controls["checkIn"].setValue(date);
    } else if (date && !checkOut && date.after(checkIn)) {
      this.form.controls["checkOut"].setValue(date);
    } else {
      this.form.controls["checkOut"].setValue(null);
      this.form.controls["checkIn"].setValue(date);
    }
    this.updateDateInput();
  }
  
  onDatepickerClosed() {
    const checkIn = this.form.controls["checkIn"].value;
    const checkOut = this.form.controls["checkOut"].value;
  
    if (checkIn && !checkOut) {
      this.form.controls["checkOut"].setValue(checkIn);
    }
    this.updateDateInput();
  }
  
  updateDateInput() {
    this.bookedRange.nativeElement.value =
      this.ngbDateParser.format(this.form.controls["checkIn"].value) +
      " - " +
      this.ngbDateParser.format(this.form.controls["checkOut"].value);
  
    this.emitDates();
  }

  isHovered(date: NgbDate) {
    return (
      this.form.controls["checkIn"].value &&
      !this.form.controls["checkOut"].value &&
      this.hoveredDate &&
      date.after(this.form.controls["checkIn"].value) &&
      date.before(this.hoveredDate)
    );
  }

  isInside(date: NgbDate) {
    return (
      date.after(this.form.controls["checkIn"].value) &&
      date.before(this.form.controls["checkOut"].value)
    );
  }

  isRange(date: NgbDate) {
    return (
      date.equals(this.form.controls["checkIn"].value) ||
      date.equals(this.form.controls["checkOut"].value)
    );
  }

  closeModalCross() {
    this.modalService.dismissAll();
  }

  navigate(route: string) {
    const navigationExtra: NavigationExtras = {
      state: {
        canRequestCustomBooking: this.canRequestCustomBooking,
        canRequestCustomFlight: this.canRequestCustomFlight,
        canRequestCustomTrain: this.canRequestCustomTrain,
        canRequestCustomCar: this.canRequestCustomCar,
        canRequestCustomAirbnb: this.canRequestCustomAirbnb,
        userUuid: this.userUuid ? this.userUuid : this.authUserUuid,
        companyUuid: this.companyUuid,
        userImageBase64: this.userImageBase64,
      },
      queryParams: {
        userId: this.userUuid ? this.userUuid : this.authUserUuid,
        companyUuid: this.companyUuid,
      },
      replaceUrl: true
    };

    this.router.navigate(["custom-services/" + route], navigationExtra);
  }

  // Buscador de ciudades
  getCities(value: string) {
    this.citiesList = [];
    if (value !== "" && value.length > 2) {
      cities.Table1.forEach((v) => {
        if (
          v["name"]?.toLowerCase().includes(value?.toLowerCase()) &&
          !this.citiesList.includes(v["name"])
        ) {
          this.citiesList.push(v["name"]);
        }
      });
    } else {
      this.citiesList = [];
    }
  }
  getHotels(value: string) {
    if (value !== "" && this.city !== "") {
      hotels.hotels.forEach((h) => {
        {
          if (
            h["city"]?.toLowerCase().includes(this.city?.toLowerCase()) &&
            !this.hotelsList.includes(h.name)
          ) {
            this.hotelsList.push(h.name);
          }
        }
      });
    } else {
      this.hotelsList = [];
    }
  }

  getCompanyUsers() {
    this.companyService.getCompanyUsers(this.companyUuid).subscribe(
      (res) => {
        this.companyUsers = res.map(user => ({
          name: user.name,
          lastname: user.lastname,
          uuid: user.uuid
        }));
        this.filteredUsers = [...this.companyUsers];
        this.filteredUsersWithoutDefault = this.filteredUsers.filter(user => user.uuid !== this.defaultUser?.uuid);
      },
      (err) => {}
    );
  }

  isFormValid(): boolean {
    const travelersPerRoom = this.getTravelersPerRoom.value;
    const allRoomsHaveTravelers = travelersPerRoom.every(room => room && room.length > 0);
    return this.form.valid && allRoomsHaveTravelers;
  }

  getOutMinDateManual() {
    return this.utilsService.getOutDate(
      this.minDateNgStruct,
      this.form.controls["checkIn"].value
    );
  }

  cleanCitiesList(option) {
    this.city = option;
    this.citiesList = [];
  }
  cleanHotelsList() {
    this.hotelsList = [];
  }

  applyFilter(filterValue: string) {
    this.filteredUsers = this.companyUsers.filter(user =>
      `${user.name} ${user.lastname}`.toLowerCase().includes(filterValue.toLowerCase())
    );
    this.filteredUsersWithoutDefault = this.companyUsers.filter(user =>
      `${user.name} ${user.lastname}`.toLowerCase().includes(filterValue.toLowerCase())
    );
  }

  clearSearch(index: number) {
    const input = this.searchInputs.toArray()[index];
    if (input) {
      input.nativeElement.value = '';
    }

    this.filteredUsers = [...this.companyUsers];
    this.filteredUsersWithoutDefault = this.filteredUsers.filter(user => user.uuid !== this.defaultUser?.uuid);
  }

  applyBackendValidationErrors(validationErrors: any[]): void {
    this.backendValidationErrors = {};

    validationErrors.forEach((err) => {
      const backendPath = err.field;

      this.backendValidationErrors[backendPath] = err.type || 'invalid';

      const controlPath = this.mapBackendPathToAngularPath(backendPath);
      const control = this.form.get(controlPath);

      if (control) {
        control.setErrors({
          ...(control.errors || {}),
          backend: err.type || 'invalid',
        });
        control.markAsTouched();
        control.markAsDirty();
      }
    });

    this.isInvalid = true;
  }

  hasBackendError(path: string): boolean {
    return !!this.backendValidationErrors[path];
  }

  getBackendError(path: string): string {
    return this.backendValidationErrors[path] || '';
  }

  private mapBackendPathToAngularPath(path: string): string {
    const map: Record<string, string> = {
      companyUuid: 'companyUuid',
      userUuid: 'userUuid',

      city: 'city',
      destinationName: 'city',

      hotel: 'hotel',
      hotelName: 'hotel',

      checkIn: 'checkIn',
      checkOut: 'checkOut',

      comments: 'comments',
      rooms: 'rooms',

      defaultTraveler: 'defaultTraveler',

      travelersPerRoom: 'travelersPerRoom',
      'travelersPerRoom.travelers': 'travelersPerRoom',
      'travelersPerRoom.roomNumber': 'travelersPerRoom',

      holder: 'holder',
      'holder.name': 'holder',
      'holder.surname': 'holder',
    };

    const normalizedPath = this.utilsService.normalizeValidationField(path);

    return map[normalizedPath] || map[path] || path;
  }

  getRoomTravelerControl(roomIndex: number): FormControl {
    return roomIndex === 0
      ? this.form.get('defaultTraveler') as FormControl
      : this.getTravelersPerRoom.at(roomIndex - 1) as FormControl;
  }

  getRoomExternalTravelers(roomIndex: number): RequestHotelTraveler[] {
    const control = this.getRoomTravelerControl(roomIndex);

    if (!control || !Array.isArray(control.value)) {
      return [];
    }

    return control.value.filter((traveler) => traveler?.type === 'external');
  }

  getTravelerDisplayName(traveler: any): string {
    return `${traveler?.name || ''} ${traveler?.lastname || traveler?.surname || ''}`.trim();
  }

  getRoomTravelerNames(roomIndex: number): string {
    const control = this.getRoomTravelerControl(roomIndex);

    if (!control || !Array.isArray(control.value)) {
      return '';
    }

    return control.value
      .map((traveler) => this.getTravelerDisplayName(traveler))
      .filter(Boolean)
      .join(', ');
  }

  private createExternalTravelerUuid(name: string, lastname: string): string {
    const raw = `${name}-${lastname}-${Date.now()}`
      .toString()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/[^a-zA-Z0-9]+/g, '-')
      .toLowerCase();

    return `external-${raw}`;
  }

  toggleExternalGuestFormForRoom(roomIndex: number): void {
    this.externalGuestModeByRoom[roomIndex] = !this.externalGuestModeByRoom[roomIndex];
  }

  cancelExternalGuestForRoom(roomIndex: number): void {
    this.externalGuestNameByRoom[roomIndex] = '';
    this.externalGuestLastnameByRoom[roomIndex] = '';
    this.externalGuestModeByRoom[roomIndex] = false;
  }

  addExternalGuestForRoom(roomIndex: number): void {
    const name = (this.externalGuestNameByRoom[roomIndex] || '').trim();
    const lastname = (this.externalGuestLastnameByRoom[roomIndex] || '').trim();

    if (!name || !lastname) {
      const messageKey = !name && !lastname
        ? 'hotel.external-guest.name-lastname-required'
        : !name
          ? 'hotel.external-guest.name-required'
          : 'hotel.external-guest.lastname-required';

      this.translate
        .get(['common.attention', messageKey])
        .pipe(take(1))
        .subscribe((result) => {
          this.ngxToastrService.typeInfo(
            result['common.attention'],
            result[messageKey]
          );
        });

      return;
    }

    const traveler: RequestHotelTraveler = {
      type: 'external',
      uuid: this.createExternalTravelerUuid(name, lastname),
      name,
      lastname,
    };

    const control = this.getRoomTravelerControl(roomIndex);

    const currentValue = Array.isArray(control?.value)
      ? control.value
      : [];

    control?.setValue([...currentValue, traveler]);
    control?.markAsDirty();
    control?.markAsTouched();
    control?.updateValueAndValidity();

    this.externalGuestNameByRoom[roomIndex] = '';
    this.externalGuestLastnameByRoom[roomIndex] = '';
    this.externalGuestModeByRoom[roomIndex] = false;
  }

  changeRoomExternalTravelerName(
    roomIndex: number,
    travelerUuid: string,
    field: 'name' | 'lastname',
    value: string
  ): void {
    const control = this.getRoomTravelerControl(roomIndex);

    if (!control || !Array.isArray(control.value)) {
      return;
    }

    const currentTravelers = control.value;

    const travelerToUpdate = currentTravelers.find(
      (traveler: RequestHotelTraveler) => traveler.uuid === travelerUuid
    );

    if (!travelerToUpdate) {
      return;
    }

    travelerToUpdate[field] = value;

    control.setValue(currentTravelers, { emitEvent: false });
    control.markAsDirty();
    control.markAsTouched();
    control.updateValueAndValidity({ emitEvent: false });
  }

  trackByTravelerUuid(index: number, traveler: RequestHotelTraveler): string {
    return traveler?.uuid || `external-traveler-${index}`;
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
